import { bytesToNDigits } from '@mail/cross-sizes-utils';
import { Spacing, Text } from '@vkontakte/vkui';
import { parse } from 'qs';
import React, { type ReactElement } from 'react';
import { useLocation } from 'react-router';
import { getPeriodName, isMonthPeriod, isValidPeriod } from 'reactApp/utils/Period';

import styles from '../PromocodeDialog.css';
import stylesMobile from '../PromocodeDialogMobile.css';

type PromocodeDetails = {
    title: ReactElement;
    titleMobile: ReactElement;
    conditions?: string[];
    promocode?: string;
};

export const usePromocodeDetails = (): PromocodeDetails => {
    const { search } = useLocation();

    const queryParams = parse(search, { ignoreQueryPrefix: true }) || {};
    const { promocode, quota, period, trial, renew, gift, percent } = queryParams;

    const commonCondition = promocode && !isNaN(Number(quota));

    const isTrial = commonCondition && isValidPeriod(trial) && isValidPeriod(renew);
    const isDiscount = commonCondition && isValidPeriod(period) && isValidPeriod(renew) && !isNaN(Number(percent));
    const isGift = commonCondition && isValidPeriod(period) && Boolean(gift);

    let title = (
        <Text className={styles.title}>
            Все возможности <span className={styles.crown}>Mail</span> <span className={styles.underline}>с&nbsp;подпиской&nbsp;</span>Mail
            Space
        </Text>
    );

    let titleMobile = (
        <Text className={stylesMobile.title}>
            <span className={stylesMobile.crownLeft}>Все</span> возможности Mail&nbsp;с&nbsp;подпиской{' '}
            <span className={stylesMobile.underline}>Mail&nbsp;Space</span>
        </Text>
    );

    let conditions;

    if (isTrial) {
        const trialName = getPeriodName(trial, true, false);
        const renewName = getPeriodName(renew, true, true);

        title = (
            <Text className={styles.title}>
                {bytesToNDigits(quota, 3).value} на <span className={styles.crown}>{getPeriodName(trial, true, true)}</span>
                <br />
                <span className={styles.underline}>бесплатно</span>
            </Text>
        );

        titleMobile = (
            <>
                <Spacing size={40} />
                <Text className={stylesMobile.title}>
                    {bytesToNDigits(quota, 3).value} на <span className={stylesMobile.crown}>{getPeriodName(trial, true, true)}</span>
                    <br />
                    <span className={stylesMobile.underline}>бесплатно</span>
                </Text>
            </>
        );

        conditions = [
            `Срок действия промокода – ${trialName}. Бесплатную подписку можно активировать в течение 1\u00A0месяца с момента активации промокода`,
            `При приобретении бесплатной подписки пользователь соглашается с\u00A0тем, что подписка продлевается автоматически сроком на ${renewName}`,
        ];
    }

    if (isDiscount) {
        const periodTypeText = isMonthPeriod(renew) ? 'месячный' : 'годовой';
        const periodName = getPeriodName(period, true, false, false, true);
        const renewName = getPeriodName(renew, true, true);

        title = (
            <Text className={styles.title}>
                Скидка {percent}% на <span className={styles.crown}>{periodTypeText}</span>
                <br />
                тариф <span className={styles.underlineTariff}>{bytesToNDigits(quota, 3).value}</span>
            </Text>
        );

        titleMobile = (
            <Text className={stylesMobile.title}>
                Скидка {percent}%<br />
                на {periodTypeText} <span className={stylesMobile.crown}>тариф</span>
                <br />
                <span className={stylesMobile.underlineTariff}>{bytesToNDigits(quota, 3).value}</span>
            </Text>
        );

        conditions = [
            `Скидка по промокоду действует в\u00A0течение ${periodName} и\u00A0не\u00A0подразумевает возврат суммы скидки в денежном эквиваленте в\u00A0любых формах`,
            `При приобретении бесплатной подписки пользователь соглашается с\u00A0тем, что подписка продлевается автоматически сроком на ${renewName}`,
        ];
    }

    if (isGift) {
        title = (
            <Text className={styles.title}>
                {bytesToNDigits(quota, 3).value} на <span className={styles.crown}>{getPeriodName(period, true, true)}</span>
                <br />
                <span className={styles.underline}>в подарок!</span>
            </Text>
        );

        titleMobile = (
            <>
                <Spacing size={40} />
                <Text className={stylesMobile.title}>
                    {bytesToNDigits(quota, 3).value} на <span className={stylesMobile.crown}>{getPeriodName(period, true, true)}</span>
                    <br />
                    <span className={stylesMobile.underline}>в подарок!</span>
                </Text>
            </>
        );
    }

    return { title, titleMobile, conditions, promocode };
};
